import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function HomePage() {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.observe-animation').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/f7ca96ebd8fa58e8ea967e890ccaef80.jpg"
            alt="Modern industrial workspace"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#14173A]/95 via-[#14173A]/85 to-[#14173A]/70"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full border border-[#D4AF37] mb-8">
              <i className="ri-map-pin-line text-[#D4AF37] text-sm"></i>
              <span className="text-[#D4AF37] text-sm font-medium">Minnesota + Wisconsin | Remote Support Available</span>
            </div>
            
            <h1 className="text-7xl font-bold text-white mb-6 leading-tight">
              Safety Built<br />Forward.
            </h1>
            
            <p className="text-xl text-gray-200 mb-12 leading-relaxed max-w-2xl">
              We help organizations build a compliant safety foundation — then strengthen culture, behaviors, and leading indicators to prevent incidents before they happen.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/contact"
                className="inline-flex items-center gap-3 px-8 py-4 bg-[#D4AF37] text-[#14173A] rounded-2xl font-semibold text-lg hover:bg-[#C9A832] transition-all duration-300 hover:scale-105 hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Request a Consultation
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
              
              <Link 
                to="/forward-system"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white rounded-2xl font-semibold text-lg hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Learn The Forward Safety System™
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Three-Step Visual Journey */}
      <section className="py-32 bg-[#F7F7F9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20 observe-animation opacity-0">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 mb-6">
              <i className="ri-compass-3-line text-[#D4AF37] text-lg"></i>
              <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wide">Our Approach</span>
            </div>
            <h2 className="text-5xl font-bold text-[#14173A] mb-4">
              Compliance First → Then Forward
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                number: '01',
                title: 'Compliance Foundation',
                description: 'OSHA requirements, documentation, and regulatory alignment as your baseline.',
                items: ['Complete regulatory audit', 'Documentation systems', 'Training compliance']
              },
              {
                number: '02',
                title: 'Culture & Daily Behaviors',
                description: 'Leadership alignment and frontline engagement that makes safety part of how work gets done.',
                items: ['Leadership coaching', 'Behavior observation', 'Frontline empowerment']
              },
              {
                number: '03',
                title: 'Leading Indicators & Continuous Improvement',
                description: 'Scorecards and proactive metrics that predict and prevent incidents before they occur.',
                items: ['Custom scorecards', 'Risk recognition', 'Continuous learning']
              }
            ].map((step, index) => (
              <div 
                key={index}
                className="observe-animation opacity-0 bg-white rounded-3xl p-10 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 relative overflow-hidden group cursor-pointer"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="absolute top-0 left-0 w-1 h-full bg-[#D4AF37] transform origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500"></div>
                
                <div className="text-8xl font-thin text-[#D4AF37] mb-6 leading-none">{step.number}</div>
                
                <h3 className="text-2xl font-bold text-[#14173A] mb-4">{step.title}</h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed">{step.description}</p>
                
                <ul className="space-y-3">
                  {step.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#D4AF37] text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Help */}
      <section className="py-32 bg-[#14173A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20 observe-animation opacity-0">
            <h2 className="text-5xl font-bold text-white mb-4">How We Help</h2>
            <p className="text-xl text-gray-300">Practical systems that fit your real work environment</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                icon: 'ri-file-list-3-line',
                title: 'Compliance & Safety Foundations',
                description: 'Get your OSHA requirements, documentation, and training programs in order. We build the baseline so you can focus on running your business.',
                link: '/services'
              },
              {
                icon: 'ri-compass-3-line',
                title: 'The Forward Safety System™',
                description: 'Our five-pillar approach to building proactive safety culture through leadership, behaviors, and leading indicators.',
                link: '/forward-system'
              },
              {
                icon: 'ri-team-line',
                title: 'Ongoing Safety Leadership Support',
                description: 'Monthly coaching, scorecard reviews, and optional quarterly site visits to keep your safety system strong and improving.',
                link: '/services'
              }
            ].map((service, index) => (
              <div 
                key={index}
                className="observe-animation opacity-0 bg-[#1A1D3F] rounded-3xl p-10 border-t-4 border-[#D4AF37] hover:bg-[#22254A] transition-all duration-300 group cursor-pointer"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-16 h-16 flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-[#D4AF37] text-6xl group-hover:scale-110 transition-transform duration-300`}></i>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-4">{service.title}</h3>
                
                <p className="text-gray-300 mb-6 leading-relaxed">{service.description}</p>
                
                <Link 
                  to={service.link}
                  className="inline-flex items-center gap-2 text-[#D4AF37] font-semibold hover:gap-4 transition-all duration-300 cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Horizon Safety */}
      <section className="py-32 bg-[#F7F7F9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-12 items-center">
            <div className="md:col-span-2 observe-animation opacity-0">
              <h2 className="text-5xl font-bold text-[#14173A] mb-6 leading-tight">
                Why Horizon Safety
              </h2>
              <div className="w-20 h-0.5 bg-[#D4AF37] mb-8"></div>
              <p className="text-lg text-gray-600 leading-relaxed mb-10">
                We believe safety isn't just about compliance checkboxes. It's about building a culture where people look out for each other, recognize risks before they become incidents, and continuously improve.
              </p>
              <Link 
                to="/contact"
                className="inline-flex items-center gap-3 px-8 py-4 bg-[#D4AF37] text-[#14173A] rounded-2xl font-semibold hover:bg-[#C9A832] transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Request Consultation
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            
            <div className="md:col-span-3 space-y-4">
              {[
                { icon: 'ri-shield-check-line', text: 'Compliance is the baseline' },
                { icon: 'ri-team-line', text: 'Culture drives outcomes' },
                { icon: 'ri-line-chart-line', text: 'Leading indicators guide improvement' },
                { icon: 'ri-tools-line', text: 'Practical systems that fit real work' },
                { icon: 'ri-user-voice-line', text: 'Coaching that builds capability' }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="observe-animation opacity-0 bg-white rounded-full px-8 py-5 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-4 group cursor-pointer"
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-[#D4AF37] text-3xl group-hover:scale-110 transition-transform duration-300`}></i>
                  </div>
                  <span className="text-lg font-semibold text-[#14173A]">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Credibility Strip */}
      <section className="py-10 bg-[#14173A]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center gap-12 md:gap-20">
            {[
              { icon: 'ri-map-pin-line', text: 'Minnesota + Wisconsin' },
              { icon: 'ri-wifi-line', text: 'Remote Support' },
              { icon: 'ri-hand-heart-line', text: 'On-site + Virtual' }
            ].map((item, index) => (
              <div key={index} className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className={`${item.icon} text-[#D4AF37] text-3xl`}></i>
                </div>
                <span className="text-white font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Block */}
      <section className="py-24 bg-gradient-to-r from-[#D4AF37] to-[#C9A832]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="text-4xl font-bold text-[#14173A] mb-3">Ready to Build Safety Forward?</h2>
              <p className="text-xl text-[#14173A]/80">Let's discuss your safety goals</p>
            </div>
            
            <Link 
              to="/contact"
              className="inline-flex items-center gap-3 px-10 py-5 bg-white text-[#14173A] rounded-2xl font-bold text-lg hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer"
            >
              Request a Consultation
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
